<template>
  <div class="input-wrapper overflow-hidden">
    <div
      class="input-border relative flex h-10 w-full"
      :class="title ? 'with-title mt-6' : ''"
      :style='width ? `min-width:${width}; max-width:${width};`: ""'>
      <div
        v-if='title'
        class="title absolute bottom-[calc(100%+0.375rem)] left-0 text-label text-text-muted">
        {{title}}
      </div>
      <div class="input field-shell w-full px-3" :class="{ 'is-disabled': disabled, 'is-readonly': readonly }">
        <span v-if="$slots.prefix" class="mr-2.5 flex shrink-0 items-center">
          <slot name="prefix"></slot>
        </span>
        <i v-if="icon"
          class="icon mr-2 flex shrink-0 items-center text-base text-text-muted"
          :class="[`tr tr-${icon}`, focused ? 'opacity-100' : 'opacity-60']"></i>
        <input
          ref="input"
          class="field-input w-full whitespace-nowrap"
          :class="focused ? 'placeholder:opacity-100' : 'placeholder:opacity-60'"
          v-model="valueIn"
          :type="type === 'number' ? 'text' : type"
          :inputmode="type === 'number' ? 'decimal' : undefined"
          :min="name === 'estimate' ? 0 : ''"
          :maxlength="maxLength"
          :placeholder="readonly ? '—' : placeholder"
          :disabled="disabled"
          :readonly="readonly"
          @keyup.enter="$emit('keyup-enter')"
          @keyup.up="$emit('keyup-up')"
          @keyup.down="$emit('keyup-down')"
          @focus="focused = true; $emit('gain-focus')"
          @blur="onBlur"
        />
        <span v-if="$slots.suffix" class="ml-2.5 flex shrink-0 items-center">
          <slot name="suffix"></slot>
        </span>
        <button
          v-if="controlButtonIcon"
          type="button"
          class="control-button flex shrink-0 cursor-pointer items-center justify-center
                 border-0 bg-transparent p-0 text-text-muted
                 transition-colors duration-150 hover:text-accent"
          @click="$emit('control-button-click')"
        >
          <component :is="controlButtonIcon" class="size-4.5" />
        </button>
        <div
          v-if="type === 'number' && !noButtons && !readonly"
          class="-mr-1.5 flex shrink-0 flex-col gap-px
                 [&>button]:flex [&>button]:h-4 [&>button]:w-5 [&>button]:cursor-pointer
                 [&>button]:items-center [&>button]:justify-center [&>button]:rounded-sm
                 [&>button]:border-0 [&>button]:bg-transparent [&>button]:p-0
                 [&>button]:text-text-muted [&>button]:transition-colors [&>button]:duration-150
                 [&>button:hover]:bg-hover [&>button:hover]:text-accent">
          <button type="button" @click="onAdd($event)">
            <IconChevronUp class="size-3" />
          </button>
          <button type="button" @click="onSubtract($event)">
            <IconChevronDown class="size-3" />
          </button>
        </div>
        <IconLockClosed16Solid v-if="readonly" class="size-4 shrink-0 text-text-muted" />
      </div>
    </div>
    <div v-if="rules && rules.length" class="warning-wrapper min-h-5 pl-3 text-label text-red">
      <div class="warning" v-if="showError && !!invalid && typeof invalid === 'string'">
        {{ invalid }}
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue'
import { IconChevronUp, IconChevronDown, IconLockClosed16Solid } from '@iconify-prerendered/vue-heroicons'

const props = defineProps({
  width: {
    type: String,
    default: '',
  },
  icon: String,
  placeholder: String,
  modelValue: {
    type: String,
    default: '',
  },
  type: {
    type: String,
    default: 'text',
  },
  initialyFocused: {
    type: Boolean,
    default: false,
  },
  forSearch: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: '',
  },
  dontBlurWhenClickedOn: {
    type: Array,
    default: () => [],
  },
  noButtons: {
    type: Boolean,
    default: false,
  },
  rules: Array,
  showError: {
    type: Boolean,
    default: true,
  },
  controlButtonIcon: Object,
  disabled: {
    type: Boolean,
    default: false,
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  name: {
    type: String,
    default: '',
  },
  maxLength: Number,
})

const emit = defineEmits([
  'keyup-enter',
  'keyup-up', 
  'keyup-down',
  'gain-focus',
  'lose-focus',
  'control-button-click',
  'validation',
  'update:modelValue'
])

// Reactive data
const valueIn = ref(props.modelValue)
const focused = ref(props.initialyFocused)
const mouseDownClickEvent = ref(null)
const input = ref(null)
let focusTimeout;
const onMouseDown = e => { mouseDownClickEvent.value = e; };
const onMouseUp = () => { mouseDownClickEvent.value = null; };

// Computed
const invalid = computed(() => {
  if (!props.rules || !props.rules.length) {
    return false;
  }

  const result = props.rules.map(r => r(valueIn.value)).find(v => !!v);
  emit('validation', result);
  return result;
})

// Methods
const onAdd = (event) => {
  event.preventDefault();
  if (valueIn.value) valueIn.value = (Number.parseInt(valueIn.value, 10) + 1).toString();
  else valueIn.value = '1';
}

const onSubtract = (event) => {
  event.preventDefault();
  if (valueIn.value) valueIn.value = (Number.parseInt(valueIn.value, 10) - 1).toString();
  else valueIn.value = '0';
}

const focus = (emitEvent = false) => {
  input.value.focus();
  focused.value = true;
  if (emitEvent) emit('gain-focus');
}

const blur = (emitEvent = false) => {
  input.value.blur();
  focused.value = false;
  if (emitEvent) emit('lose-focus');
}

const onBlur = () => {
  let shouldLoseFocus = true;
  if (mouseDownClickEvent.value) {
    const clickedAt = mouseDownClickEvent.value.target
      && mouseDownClickEvent.value.target.offsetParent
      && mouseDownClickEvent.value.target.offsetParent.className.split(' ');
    shouldLoseFocus = clickedAt && !props.dontBlurWhenClickedOn
      .find(dbwco => clickedAt.includes(dbwco));
  }
  if (shouldLoseFocus) {
    focused.value = false;
    emit('lose-focus');
  } else {
    input.value.focus();
  }
}

// Lifecycle
onMounted(() => {
  document.addEventListener('mousedown', onMouseDown);
  document.addEventListener('mouseup', onMouseUp);
  if (props.initialyFocused) {
    emit('gain-focus');
    focusTimeout = setTimeout(() => input.value.focus()); // nextTick does not focus every case (e.g. tagfocus)
  }
})

onBeforeUnmount(() => {
  document.removeEventListener('mousedown', onMouseDown);
  document.removeEventListener('mouseup', onMouseUp);
  clearTimeout(focusTimeout);
})

// Watchers
watch(() => props.modelValue, () => {
  valueIn.value = props.modelValue;
})

watch(valueIn, () => {
  if (props.name === 'estimate') {
    if (valueIn.value < 0) valueIn.value = '0';
    if (props.modelValue === valueIn.value) return;
  }
  emit('update:modelValue', valueIn.value);
})

// Expose methods for template refs
defineExpose({
  focus,
  blur
})
</script>

